import { c as _c } from "react/compiler-runtime";
import { useMutation, useQuery } from '@apollo/client/react';
import { Box, Button, HStack, Text, VStack } from '@chakra-ui/react';
import { AgreementListRow } from '@src/components/sco/Advisors/cards/AgreementListRow';
import { useAdvisorAgreementPdfDownload } from '@src/components/sco/Advisors/hooks/useAdvisorAgreementPdfDownload';
import { useAdvisorsCollectionLoadMore } from '@src/components/sco/Advisors/hooks/useAdvisorsCollectionLoadMore';
import { useCreateAdvisorAgreement } from '@src/components/sco/Advisors/hooks/useCreateAdvisorAgreement';
import { AgreementsLayout } from '@src/components/sco/Advisors/layout/AgreementsLayout';
import { useAdvisorsPaths } from '@src/components/sco/Advisors/paths';
import { AdvisorsLoadMore } from '@src/components/sco/Advisors/shared/AdvisorsLoadMore';
import { AdvisorsQueryState } from '@src/components/sco/Advisors/shared/AdvisorsQueryState';
import type { AdvisorAgreementsQueryResult } from '@src/components/sco/Advisors/types/advisorAgreement';
import { AGREEMENT_STATUS_DECLINED, AGREEMENT_STATUS_SIGNED } from '@src/components/sco/Advisors/utils/agreementKeys';
import { advisorAgreementsListVariables, advisorAgreementsWaitingVariables, countAgreementStatusFilters, groupAgreementListItems, listHasBothPartyRoles, rowsForAgreementStatusFilter, toMergedAgreementListItems } from '@src/components/sco/Advisors/utils/agreementList';
import { DELETE_UNSIGNED_AGREEMENT_CONFIRM } from '@src/components/sco/Advisors/utils/agreementSend';
import { AGREEMENT_STATUS_FILTER_COPY, AGREEMENT_STATUS_FILTER_LABELS, agreementsNavItems, parseAgreementStatusFilter } from '@src/components/sco/Advisors/utils/agreementsNav';
import { NextLink } from '@src/components/sco/utilities/NextLink';
import { DELETE_ADVISOR_AGREEMENT } from '@src/gql/mutations/DeleteAdvisorAgreement';
import { RESPOND_TO_ADVISOR_AGREEMENT } from '@src/gql/mutations/RespondToAdvisorAgreement';
import { SYNC_ADVISOR_AGREEMENT_SIGNATURE } from '@src/gql/mutations/SyncAdvisorAgreementSignature';
import { ADVISOR_AGREEMENTS } from '@src/gql/queries/AdvisorAgreements';
import { ADVISORS_ATTENTION_REFETCH } from '@src/gql/queries/AdvisorsAttention';
import { useRouter } from 'next/router';
import { useMemo, useState, type ReactNode } from 'react';
import { LuCalculator, LuFileText, LuSearch, LuSquarePen } from 'react-icons/lu';

/** Every row action can clear a your-turn item, so the waiting lists and the badges refetch too. */
const LIST_REFETCH = [{
  query: ADVISOR_AGREEMENTS,
  variables: advisorAgreementsListVariables('FOUNDER')
}, {
  query: ADVISOR_AGREEMENTS,
  variables: advisorAgreementsListVariables('ADVISOR')
}, {
  query: ADVISOR_AGREEMENTS,
  variables: advisorAgreementsWaitingVariables('FOUNDER')
}, {
  query: ADVISOR_AGREEMENTS,
  variables: advisorAgreementsWaitingVariables('ADVISOR')
}, ADVISORS_ATTENTION_REFETCH];
const StatusGroup = t0 => {
  const $ = _c(11);
  const {
    label,
    color,
    children
  } = t0;
  const t1 = `${color}.solid`;
  const t2 = `0 0 8px var(--chakra-colors-${color}-solid)`;
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = <Box bgColor={t1} borderRadius="full" boxShadow={t2} boxSize={2.5} />;
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== label) {
    t4 = <Text color="colorPalette.fg/60" textStyle="labelXs">{label}</Text>;
    $[3] = label;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t3 || $[6] !== t4) {
    t5 = <HStack gap={2}>{t3}{t4}</HStack>;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== children || $[9] !== t5) {
    t6 = <VStack align="stretch" gap={3} marginTop={4}>{t5}{children}</VStack>;
    $[8] = children;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
};
export const AgreementsSection = () => {
  const paths = useAdvisorsPaths();
  const router = useRouter();
  const [respondLoadingId, setRespondLoadingId] = useState<string | null>(null);
  const [checkStatusId, setCheckStatusId] = useState<string | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [statusNote, setStatusNote] = useState<string | null>(null);
  const statusFilter = parseAgreementStatusFilter(router.query.status);
  const pdf = useAdvisorAgreementPdfDownload();
  const createAgreement = useCreateAdvisorAgreement();
  const founderQuery = useQuery<AdvisorAgreementsQueryResult>(ADVISOR_AGREEMENTS, {
    variables: advisorAgreementsListVariables('FOUNDER'),
    fetchPolicy: 'network-only',
    skip: !router.isReady
  });
  const advisorQuery = useQuery<AdvisorAgreementsQueryResult>(ADVISOR_AGREEMENTS, {
    variables: advisorAgreementsListVariables('ADVISOR'),
    fetchPolicy: 'network-only',
    skip: !router.isReady
  });

  // "Waiting on you" (L20): server-filtered, both roles, only while that view is open.
  const showWaiting = statusFilter === 'waiting';
  const founderWaitingQuery = useQuery<AdvisorAgreementsQueryResult>(ADVISOR_AGREEMENTS, {
    variables: advisorAgreementsWaitingVariables('FOUNDER'),
    fetchPolicy: 'network-only',
    skip: !router.isReady || !showWaiting
  });
  const advisorWaitingQuery = useQuery<AdvisorAgreementsQueryResult>(ADVISOR_AGREEMENTS, {
    variables: advisorAgreementsWaitingVariables('ADVISOR'),
    fetchPolicy: 'network-only',
    skip: !router.isReady || !showWaiting
  });
  const founderFirstPage = founderQuery.data?.advisorAgreements?.items ?? [];
  const advisorFirstPage = advisorQuery.data?.advisorAgreements?.items ?? [];
  const founderLoadMore = useAdvisorsCollectionLoadMore({
    fetchMore: founderQuery.fetchMore,
    field: 'advisorAgreements',
    firstPageItems: founderFirstPage,
    hasNextPage: founderQuery.data?.advisorAgreements?.pagination?.hasNextPage
  });
  const advisorLoadMore = useAdvisorsCollectionLoadMore({
    fetchMore: advisorQuery.fetchMore,
    field: 'advisorAgreements',
    firstPageItems: advisorFirstPage,
    hasNextPage: advisorQuery.data?.advisorAgreements?.pagination?.hasNextPage
  });
  const founderWaitingLoadMore = useAdvisorsCollectionLoadMore({
    fetchMore: founderWaitingQuery.fetchMore,
    field: 'advisorAgreements',
    firstPageItems: founderWaitingQuery.data?.advisorAgreements?.items ?? [],
    hasNextPage: founderWaitingQuery.data?.advisorAgreements?.pagination?.hasNextPage
  });
  const advisorWaitingLoadMore = useAdvisorsCollectionLoadMore({
    fetchMore: advisorWaitingQuery.fetchMore,
    field: 'advisorAgreements',
    firstPageItems: advisorWaitingQuery.data?.advisorAgreements?.items ?? [],
    hasNextPage: advisorWaitingQuery.data?.advisorAgreements?.pagination?.hasNextPage
  });
  const [respondMutation] = useMutation(RESPOND_TO_ADVISOR_AGREEMENT, {
    refetchQueries: LIST_REFETCH
  });
  const [syncSignature] = useMutation(SYNC_ADVISOR_AGREEMENT_SIGNATURE);
  const [deleteAgreement] = useMutation(DELETE_ADVISOR_AGREEMENT);
  const rows = useMemo(() => toMergedAgreementListItems(founderLoadMore.items, advisorLoadMore.items), [advisorLoadMore.items, founderLoadMore.items]);
  const waitingRows = useMemo(() => toMergedAgreementListItems(founderWaitingLoadMore.items, advisorWaitingLoadMore.items), [advisorWaitingLoadMore.items, founderWaitingLoadMore.items]);
  const showPartyBadge = listHasBothPartyRoles(rows);
  const counts = useMemo(() => countAgreementStatusFilters(rows), [rows]);
  const groups = useMemo(() => groupAgreementListItems(rows), [rows]);
  const visibleRows = useMemo(() => rowsForAgreementStatusFilter(showWaiting ? waitingRows : rows, statusFilter), [rows, showWaiting, statusFilter, waitingRows]);
  const visibleGroups = statusFilter === 'all' ? groups : groups.filter(group => group.key === statusFilter);
  const handleRespond = async (id: string, action: 'DECLINE' | 'REQUEST_CHANGES') => {
    setRespondLoadingId(id);
    try {
      await respondMutation({
        variables: {
          input: {
            advisorAgreementId: id,
            action
          }
        }
      });
    } finally {
      setRespondLoadingId(null);
    }
  };
  const handleCheckStatus = async (id_0: string) => {
    setCheckStatusId(id_0);
    setStatusNote(null);
    try {
      const result = await syncSignature({
        variables: {
          input: {
            advisorAgreementId: id_0
          }
        },
        refetchQueries: LIST_REFETCH
      });
      const next = result.data?.syncAdvisorAgreementSignature?.advisorAgreement;
      if (next?.statusKey === AGREEMENT_STATUS_SIGNED) {
        setStatusNote('Both parties signed in Documenso. The signed PDF is attached.');
      } else if (next?.statusKey === AGREEMENT_STATUS_DECLINED) {
        setStatusNote('Documenso shows this envelope was declined.');
      } else {
        setStatusNote('Still waiting in Documenso. Sign, then check status again.');
      }
    } catch {
      setStatusNote('Could not check Documenso status.');
    } finally {
      setCheckStatusId(null);
    }
  };
  const handleDelete = async (id_1: string) => {
    if (!window.confirm(DELETE_UNSIGNED_AGREEMENT_CONFIRM)) {
      return;
    }
    setDeleteId(id_1);
    setStatusNote(null);
    try {
      await deleteAgreement({
        variables: {
          input: {
            advisorAgreementId: id_1
          }
        },
        refetchQueries: LIST_REFETCH
      });
    } catch {
      setStatusNote('Could not delete this agreement.');
    } finally {
      setDeleteId(null);
    }
  };
  const listQueries = showWaiting ? [founderWaitingQuery, advisorWaitingQuery] : [founderQuery, advisorQuery];
  const loading = listQueries.some(query => query.loading && !query.data);
  const error = listQueries.find(query_0 => query_0.error)?.error;
  const empty = AGREEMENT_STATUS_FILTER_COPY[statusFilter];
  const title = statusFilter === 'all' ? 'FAST Agreements' : AGREEMENT_STATUS_FILTER_LABELS[statusFilter];
  const description = agreementsNavItems(paths).find(item => item.key === statusFilter)?.description ?? empty.emptyBody;
  const [founderPager, advisorPager] = showWaiting ? [founderWaitingLoadMore, advisorWaitingLoadMore] : [founderLoadMore, advisorLoadMore];
  const loadMore = <AdvisorsLoadMore hasNextPage={founderPager.hasNextPage || advisorPager.hasNextPage} loading={founderPager.loadingMore || advisorPager.loadingMore} onLoadMore={() => {
    if (founderPager.hasNextPage) {
      void founderPager.loadMore();
    }
    if (advisorPager.hasNextPage) {
      void advisorPager.loadMore();
    }
  }} />;
  const renderRow = (item_0: (typeof visibleRows)[number]) => <AgreementListRow key={item_0.advisorAgreementId} checkingStatus={checkStatusId === item_0.advisorAgreementId} deleting={deleteId === item_0.advisorAgreementId} downloading={pdf.busyId === item_0.advisorAgreementId} respondLoading={respondLoadingId === item_0.advisorAgreementId} row={item_0} showPartyBadge={showPartyBadge} signHref={paths.sign} onCheckStatus={handleCheckStatus} onDelete={handleDelete} onRespond={handleRespond} onDownloadPdf={id_2 => void pdf.downloadAgreementPdf(id_2, item_0.statusKey === AGREEMENT_STATUS_SIGNED ? undefined : 'generated')} />;
  return <AgreementsLayout activeKey={statusFilter} counts={counts} description={description} title={title}>
      {pdf.error || createAgreement.error ? <Text color={'red.fg'} fontSize={'sm'}>
          {pdf.error || createAgreement.error}
        </Text> : statusNote ? <Text color={'colorPalette.fg/70'} fontSize={'sm'}>
          {statusNote}
        </Text> : null}

      <AdvisorsQueryState error={error} loading={loading}>
        {visibleRows.length === 0 ? <VStack align={'stretch'} borderColor={'colorPalette.fg/20'} borderRadius={'lg'} borderStyle={'dashed'} borderWidth={'1px'} gap={3} padding={6}>
            <Text fontWeight={'semibold'}>{empty.emptyTitle}</Text>
            <Text color={'colorPalette.fg/60'} fontSize={'sm'}>
              {empty.emptyBody}
            </Text>
            <HStack gap={2} wrap={'wrap'}>
              {empty.cta ? <Button loading={createAgreement.loading} size={'sm'} variant={'cta'} onClick={() => void createAgreement.create()}>
                  <LuFileText /> {empty.cta}
                </Button> : null}
              <Button asChild size={'sm'} variant={'outline'}>
                <NextLink href={paths.directory}>
                  <LuSearch /> Find advisors
                </NextLink>
              </Button>
              <Button asChild size={'sm'} variant={'outline'}>
                <NextLink href={paths.seatNew}>
                  <LuSquarePen /> Open a board seat
                </NextLink>
              </Button>
              <Button asChild size={'sm'} variant={'ghost'}>
                <NextLink href={paths.compCalculator}>
                  <LuCalculator /> Compensation Calculator
                </NextLink>
              </Button>
            </HStack>
          </VStack> : statusFilter === 'all' ? <>
            {visibleGroups.map(group_0 => <StatusGroup key={group_0.key} color={group_0.color} label={`${group_0.label} · ${group_0.rows.length}`}>
                {group_0.rows.map(renderRow)}
              </StatusGroup>)}
            {loadMore}
          </> : <>
            <VStack align={'stretch'} gap={3}>
              {visibleRows.map(renderRow)}
            </VStack>
            {loadMore}
          </>}
      </AdvisorsQueryState>
    </AgreementsLayout>;
};